import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import PPCAVerificationTab from "./PPCAVerificationTab";
import { COURSES as COURSES_SEMANTIC } from "./courseCatalog";
import { PLANS as PLANS_SEMANTIC } from "./membershipPlans";
import { getStateFlags, findStateMeta } from "./stateGroups";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const LIGHT_C = {
  page:"#f8fafc", surface:"#ffffff", surfaceAlt:"#f1f5f9",
  navy:"#0d1f3c", navyMid:"#1a3358",
  blue:"#1e56a8", blueLight:"#2563eb", blueSoft:"#dbeafe", blueGhost:"#eff6ff",
  gold:"#c9971c", goldLight:"#fef9ec", goldSoft:"#fde68a",
  green:"#059669", greenLight:"#dcfce7",
  amber:"#d97706", amberLight:"#fef3c7",
  // Red replaced with vivid rose — stands out but matches premium palette
  red:"#f43f5e", redLight:"#fff1f2",
  purple:"#7c3aed", purpleLight:"#f5f3ff",
  teal:"#0891b2", tealLight:"#e0f2fe",
  text:"#0d1f3c", textMid:"#334155", textSub:"#475569", textLight:"#64748b",
  border:"#e2e8f0", white:"#ffffff",
};
const DARK_C = {
  page:"#0d1f3c", surface:"#142a4d", surfaceAlt:"#1a3358",
  navy:"#0d1f3c", navyMid:"#1a3358",
  blue:"#60a5fa", blueLight:"#93c5fd", blueSoft:"rgba(96,165,250,0.15)", blueGhost:"rgba(96,165,250,0.08)",
  gold:"#fbbf24", goldLight:"rgba(251,191,36,0.12)", goldSoft:"#fde68a",
  green:"#34d399", greenLight:"rgba(52,211,153,0.12)",
  amber:"#fbbf24", amberLight:"rgba(251,191,36,0.12)",
  red:"#fb7185", redLight:"rgba(251,113,133,0.12)",
  purple:"#c4b5fd", purpleLight:"rgba(196,181,253,0.12)",
  teal:"#22d3ee", tealLight:"rgba(34,211,238,0.12)",
  text:"#f0f4f8", textMid:"rgba(240,244,248,0.85)", textSub:"rgba(240,244,248,0.65)", textLight:"rgba(240,244,248,0.50)",
  border:"rgba(255,255,255,0.10)", white:"#ffffff",
};
// C is set dynamically inside components that receive isDarkMode
let C = LIGHT_C;

// ─── DATA ─────────────────────────────────────────────────────────────────────
// Resolve each catalog's semantic colorKey into this page's actual C.xxx
// token — done once here, so CourseCard/MembershipCard/StateMap below are
// completely unchanged and still just read `.color`/`.badgeColor` etc.
// NOTE: this runs after `C = isDarkMode ? DARK_C : LIGHT_C` is set inside
// the TrainingHub component, so course/membership colors correctly follow
// light/dark mode too (see buildCourses()/buildMemberships() below).
const COLOR_HEX = (key) => ({
  green: C.green, teal: C.teal, amber: C.amber, blue: C.blue,
  purple: C.purple, muted: C.textLight, bundle: "#f97316",
}[key] || C.textLight);

// Picks readable text color (navy or white) for a given background hex,
// based on perceived luminance — replaces brittle hardcoded hex-string
// comparisons that silently broke contrast whenever the theme (light/dark)
// changed the actual resolved color value.
function readableTextOn(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#ffffff";
  const r = parseInt(h.slice(0,2), 16), g = parseInt(h.slice(2,4), 16), b = parseInt(h.slice(4,6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#0d1f3c" : "#ffffff";
}

function buildCourses() {
  return COURSES_SEMANTIC.map((c) => ({
    ...c,
    badgeColor: COLOR_HEX(c.colorKey),
    tagColor: COLOR_HEX(c.colorKey),
    color: COLOR_HEX(c.colorKey),
    colorL: c.colorKey === "bundle" ? "#fff7ed" : `${COLOR_HEX(c.colorKey)}22`,
  }));
}

function buildMemberships() {
  return PLANS_SEMANTIC.map((p) => ({
    ...p,
    color: COLOR_HEX(p.colorKey),
    features: p.features.map((f) => `✅ ${f}`),
    missing: p.locked,
  }));
}

// The hand-drawn SVG map only has paths for this subset of states (see
// STATE_PATHS below). Classification (network / independent / no-cert)
// is always derived from the verified shared data — never hand-copied —
// which is exactly the bug that caused WA/OR/TX to be mislabeled before.
const MAP_STATE_FLAVOR = {
  WA:{ flag:"🌲", note:"Origin state of the PEVO certification and the 13-state reciprocity network." },
  OR:{ flag:"🌲", note:"Requires its own state certification — not part of the 13-state reciprocity network. Confirm current requirements with ODOT." },
  CA:{ flag:"🌉", note:"No operator certification required by law — but a dense CHP/Caltrans permit system (weekend/holiday travel bans, tiered escort triggers) applies instead." },
  NV:{ flag:"🎰", note:"No certification required. Confirm current escort trigger dimensions with NDOT." },
  AZ:{ flag:"🌵", note:"Full reciprocity both directions. $1,000,000 CSL insurance minimum." },
  NM:{ flag:"🌶️", note:"No certification required. Confirm current escort trigger dimensions with NMDOT." },
  CO:{ flag:"🏔️", note:"Full reciprocity both directions. Mountain routes may require additional permits." },
  UT:{ flag:"⛷️", note:"Full reciprocity both directions. Also requires a separate permit for the escort vehicle itself." },
  TX:{ flag:"⭐", note:"Accepts the network certificate — Texas only formally requires a flagger certification, which the network cert satisfies." },
  OK:{ flag:"🛢️", note:"Full reciprocity both directions. $500,000 insurance minimum." },
  KS:{ flag:"🌾", note:"Accepts the network cert. Kansas has no full certification program of its own — also requires an additional KDOT permit registration." },
  MN:{ flag:"❄️", note:"Full reciprocity both directions. Minneapolis/St. Paul & Duluth metro rush-hour restrictions apply." },
  WI:{ flag:"🧀", note:"No certification required. Confirm current escort trigger dimensions with WisDOT." },
  IL:{ flag:"🌽", note:"No certification required. $500,000 insurance minimum." },
  IN:{ flag:"🏎️", note:"No certification required." },
  OH:{ flag:"✈️", note:"No certification required. Confirm current escort trigger dimensions with Ohio DOT." },
  PA:{ flag:"🏗️", note:"Accepts the network cert, but only for Superloads, plus a defensive-driving and flagger course. Does not accept certifications the other direction." },
  NY:{ flag:"🗽", note:"Zero reciprocity — confirmed. Does not accept any other state's certification, and no other state accepts New York's." },
  VA:{ flag:"🏛️", note:"Full reciprocity both directions. $1,000,000 CSL insurance minimum." },
  NC:{ flag:"⭐", note:"Full reciprocity both directions." },
  GA:{ flag:"🍑", note:"Full reciprocity both directions. $500,000 insurance minimum." },
  FL:{ flag:"🌴", note:"Accepts the network cert, but requires an additional defensive-driving course. Does not accept certifications the other direction." },
  TN:{ flag:"🎵", note:"No certification required." },
  KY:{ flag:"🏇", note:"No certification required." },
  MO:{ flag:"🏛️", note:"No certification required." },
  IA:{ flag:"🌽", note:"No certification required." },
  NE:{ flag:"🌾", note:"No certification required." },
  SD:{ flag:"🏔️", note:"Requires its own state certification — not part of the 13-state reciprocity network. Confirm current requirements with SDDOT." },
  ND:{ flag:"🌻", note:"No certification required." },
  MT:{ flag:"🦌", note:"No certification required." },
  WY:{ flag:"🦬", note:"No certification required." },
  ID:{ flag:"🥔", note:"No certification required." },
  LA:{ flag:"🎷", note:"No certification required." },
  MD:{ flag:"🦀", note:"No certification required." },
  MA:{ flag:"🦃", note:"No certification required." },
  ME:{ flag:"🦞", note:"No certification required." },
  WV:{ flag:"⛏️", note:"No certification required." },
};

function buildStatesData() {
  const out = {};
  for (const code of Object.keys(MAP_STATE_FLAVOR)) {
    const meta = findStateMeta(code);
    const flags = getStateFlags(code);
    out[code] = {
      name: meta?.name || code,
      inNetwork: flags.inReciprocityNetwork,
      independent: flags.independentCert,
      noCert: flags.noCertRequired,
      flag: MAP_STATE_FLAVOR[code].flag,
      note: MAP_STATE_FLAVOR[code].note,
    };
  }
  return out;
}

// Theme-independent, so this is safe to compute once at module load — the
// standalone StateMap component below reads it directly via closure.
const STATES_DATA = buildStatesData();


const TABS = [
  { id:"courses",    label:"Online Courses",     icon:"🎓" },
  { id:"overview",   label:"Overview & Map",     icon:"🗺️" },
  { id:"states",     label:"All State Certs",    icon:"📋" },
  { id:"how",        label:"PPCA & Verified Badge", icon:"✅" },
  { id:"cheatsheet", label:"Cheat Sheets",       icon:"🔒" },
  { id:"membership", label:"Membership & Pricing",icon:"💳" },
  { id:"features",   label:"Platform Features",  icon:"⚙️" },
];

// ─── UI PRIMITIVES ────────────────────────────────────────────────────────────
function Badge({ text, color, light }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background: light || `${color}18`, color, border:`1px solid ${color}25`, whiteSpace:"nowrap" }}>
      {text}
    </span>
  );
}

function StarRow({ rating, count }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
      <div style={{ display:"flex", gap:2 }}>
        {[1,2,3,4,5].map(s => (
          <span key={s} style={{ fontSize:12, color: s <= Math.floor(rating) ? "#f59e0b" : s <= rating ? "#f59e0b" : "#e2e8f0" }}>★</span>
        ))}
      </div>
      <span style={{ fontSize:12, fontWeight:700, color:C.gold }}>{rating}</span>
      <span style={{ fontSize:11, color:C.textLight }}>({count.toLocaleString()} students)</span>
    </div>
  );
}

function Disclaimer() {
  return (
    <div style={{ marginTop:28, padding:"14px 18px", borderRadius:12, background:C.surfaceAlt, border:`1px solid ${C.border}`, fontSize:11.5, color:C.textLight, lineHeight:1.7 }}>
      <strong style={{ color:C.textSub }}>Disclaimer: </strong>
      Certification requirements vary by state and change over time. PilotLink USA provides training, study tools, and reference material — it does not issue state licenses or official certifications. You are responsible for complying with your state's laws and DOT regulations. Information here is guidance only; always verify current requirements with your state's official DOT before operating.
    </div>
  );
}

// ─── COURSE CARD ──────────────────────────────────────────────────────────────
function CourseCard({ course, onBuy }) {
  const [showAll, setShowAll] = useState(false);
  const savings = Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100);

  return (
    <div style={{
      background:C.surface, borderRadius:20, overflow:"hidden",
      border: course.highlight ? `2px solid ${course.color}` : `1px solid ${C.border}`,
      boxShadow: course.highlight ? `0 0 0 4px ${course.colorL}, 0 8px 28px rgba(13,31,60,0.12)` : "0 2px 8px rgba(13,31,60,0.07)",
      display:"flex", flexDirection:"column", transition:"all .2s", position:"relative",
    }}
    onMouseEnter={e => { e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow=course.highlight?`0 0 0 4px ${course.colorL}, 0 16px 40px rgba(13,31,60,0.16)`:"0 8px 28px rgba(13,31,60,0.13)"; }}
    onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow=course.highlight?`0 0 0 4px ${course.colorL}, 0 8px 28px rgba(13,31,60,0.12)`:"0 2px 8px rgba(13,31,60,0.07)"; }}>

      {/* Header stripe */}
      <div style={{ background:`linear-gradient(135deg, ${course.color}, ${course.color}cc)`, padding:"20px 20px 16px", position:"relative" }}>
        {course.highlight && (
          <div style={{ position:"absolute", top:0, right:0, background:"#c9971c", color:"#0d1f3c", fontSize:10, fontWeight:800, padding:"4px 12px", borderRadius:"0 0 0 10px", letterSpacing:".08em" }}>
            BEST SELLER
          </div>
        )}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
          <span style={{ fontSize:11, fontWeight:800, color:"#fff", background:"rgba(255,255,255,0.25)", padding:"3px 9px", borderRadius:99 }}>{course.tag}</span>
          <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.8)" }}>Save {savings}%</span>
        </div>
        <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18, fontWeight:800, color:"#fff", lineHeight:1.25, marginBottom:4 }}>{course.title}</h3>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", fontWeight:500 }}>{course.subtitle}</div>
      </div>

      <div style={{ padding:"16px 20px 20px", flex:1, display:"flex", flexDirection:"column", gap:14 }}>
        {/* Rating */}
        <StarRow rating={course.rating} count={course.students} />

        {/* Stats */}
        <div style={{ display:"flex", gap:16 }}>
          {[["⏱", course.duration], ["📚", `${course.modules} modules`]].map(([icon, val]) => (
            <div key={val} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:C.textSub }}>
              <span>{icon}</span><span style={{ fontWeight:600 }}>{val}</span>
            </div>
          ))}
          <Badge text={course.badge} color={course.badgeColor} />
        </div>

        {/* Desc */}
        <p style={{ fontSize:13, color:C.textMid, lineHeight:1.7, margin:0 }}>{course.desc}</p>

        {/* Includes */}
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:C.textSub, letterSpacing:".08em", marginBottom:8 }}>WHAT'S INCLUDED</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 12px" }}>
            {(showAll ? course.includes : course.includes.slice(0,4)).map((item, i) => (
              <div key={i} style={{ display:"flex", gap:6, fontSize:12, color:C.textMid, alignItems:"flex-start" }}>
                <span style={{ color:course.color, fontWeight:800, flexShrink:0, marginTop:1 }}>✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          {course.includes.length > 4 && (
            <button onClick={() => setShowAll(p=>!p)} style={{ marginTop:8, fontSize:12, color:course.color, fontWeight:700, background:"none", border:"none", cursor:"pointer", padding:0 }}>
              {showAll ? "Show less ↑" : `+ ${course.includes.length - 4} more included →`}
            </button>
          )}
        </div>

        {/* Outcomes */}
        <div style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textSub, letterSpacing:".08em", marginBottom:8 }}>YOU'LL BE ABLE TO</div>
          {course.outcomes.map((o, i) => (
            <div key={i} style={{ display:"flex", gap:7, fontSize:12, color:C.textMid, marginBottom: i < course.outcomes.length-1 ? 4 : 0 }}>
              <span style={{ color:course.color, fontWeight:800 }}>→</span><span>{o}</span>
            </div>
          ))}
        </div>

        {/* Price + CTA */}
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:14, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <div>
            <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:28, fontWeight:900, color:C.text }}>${course.price}</span>
              <span style={{ fontSize:14, color:C.textSub, textDecoration:"line-through" }}>${course.originalPrice}</span>
            </div>
            <div style={{ fontSize:11, color:C.green, fontWeight:700 }}>One-time payment</div>
          </div>
          <button onClick={() => onBuy(course)} style={{
            background: `linear-gradient(135deg,${course.color},${course.color}dd)`,
            color: readableTextOn(course.color),
            border:"none", borderRadius:99, padding:"12px 24px", fontSize:14, fontWeight:800, cursor:"pointer",
            boxShadow:`0 4px 14px ${course.color}50`, transition:"all .15s", whiteSpace:"nowrap",
          }}
          onMouseEnter={e => { e.currentTarget.style.filter="brightness(1.08)"; e.currentTarget.style.transform="translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.filter="none"; e.currentTarget.style.transform="none"; }}>
            {course.ctaText}
          </button>
        </div>

        {/* Pass guarantee */}
        <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:C.textSub }}>
          <span style={{ color:C.green }}>🛡️</span>
          <span>Pass guarantee · 30-day money-back if not satisfied</span>
        </div>
      </div>
    </div>
  );
}

// ─── MEMBERSHIP CARD ──────────────────────────────────────────────────────────
function MembershipCard({ m }) {
  return (
    <div style={{
      background:C.surface, border: m.badge ? `2px solid ${m.color}` : `1px solid ${C.border}`,
      borderRadius:18, padding:"28px 24px", display:"flex", flexDirection:"column", gap:16,
      boxShadow: m.badge ? `0 0 0 4px ${m.color}15, 0 8px 24px rgba(13,31,60,0.10)` : "0 2px 8px rgba(13,31,60,0.06)",
      position:"relative",
    }}>
      {m.badge && (
        <div style={{ position:"absolute", top:-1, left:"50%", transform:"translateX(-50%)", background:m.color, color:"#fff", fontSize:10, fontWeight:800, padding:"5px 16px", borderRadius:"0 0 10px 10px", letterSpacing:".08em" }}>
          {m.badge.toUpperCase()}
        </div>
      )}
      {m.badge && <div style={{ height:16 }} />}

      <div>
        <div style={{ fontSize:13, fontWeight:700, color:m.color, letterSpacing:".06em", marginBottom:6 }}>{m.label.toUpperCase()}</div>
        <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
          <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:36, fontWeight:900, color:C.text }}>{m.price}</span>
          {m.period && <span style={{ fontSize:14, color:C.textSub }}>{m.period}</span>}
        </div>
      </div>

      <div style={{ flex:1 }}>
        {m.features.map((f, i) => (
          <div key={i} style={{ display:"flex", gap:8, padding:"5px 0", borderBottom: i < m.features.length-1 ? `1px solid ${C.border}` : "none", fontSize:13, color: f.startsWith("✅") ? C.textMid : C.textLight }}>
            {!f.startsWith("✅") && <span style={{ color:C.textLight, flexShrink:0 }}>—</span>}
            <span>{f}</span>
          </div>
        ))}
        {m.missing && m.missing.map((f, i) => (
          <div key={i} style={{ display:"flex", gap:8, padding:"5px 0", borderBottom:`1px solid ${C.border}`, fontSize:13, color:C.textLight }}>
            <span style={{ flexShrink:0 }}>✕</span><span style={{ textDecoration:"line-through" }}>{f}</span>
          </div>
        ))}
      </div>

      {m.note && <div style={{ fontSize:11, color:C.textSub, background:C.surfaceAlt, padding:"8px 12px", borderRadius:8 }}>{m.note}</div>}

      <Link to="/signup" style={{
        padding:"13px", borderRadius:10, border: m.ctaStyle==="ghost" ? `1.5px solid ${C.border}` : "none",
        background: m.ctaStyle==="ghost" ? "transparent" : m.color, color: m.ctaStyle==="ghost" ? C.textMid : "#fff",
        fontSize:14, fontWeight:800, cursor:"pointer",
        boxShadow: m.ctaStyle!=="ghost" ? `0 4px 14px ${m.color}30` : "none",
      }}>
        {m.cta}
      </Link>
    </div>
  );
}

// ─── CERTIFICATION POLICY (PPCA & State Exam) ─────────────────────────────────
function PolicyBlock({ title, children }) {
  return (
    <div style={{ marginBottom:18 }}>
      <div style={{ fontSize:13.5, fontWeight:800, color:C.text, marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:12.5, color:C.textMid, lineHeight:1.75 }}>{children}</div>
    </div>
  );
}

// Visual access timeline — one path for PPCA (auto-issued badge, no expiry),
// one for a State Exam cert (upload + expiring Learning Cycle). Each step is
// a TimelineStep: numbered, connected, and color-coded to its path.
function AccessTimeline({ title, color, steps }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 22px" }}>
      <div style={{ fontSize:11, fontWeight:800, color, letterSpacing:".08em", marginBottom:16 }}>{title.toUpperCase()}</div>
      {steps.map((step, i) => (
        <div key={i} style={{ display:"flex", gap:14, alignItems:"flex-start", paddingBottom: i < steps.length-1 ? 14 : 0 }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0 }}>
            <div style={{ width:28, height:28, borderRadius:"50%", background:`${color}18`, border:`1.5px solid ${color}`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, color }}>
              {i+1}
            </div>
            {i < steps.length-1 && <div style={{ width:2, flex:1, minHeight:22, background:C.border, margin:"2px 0" }} />}
          </div>
          <div style={{ paddingTop:3 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:2 }}>{step.title}</div>
            <div style={{ fontSize:12, color:C.textSub, lineHeight:1.6 }}>{step.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CertificationPolicySection() {
  const ppcaSteps = [
    { title:"Take the PPCA exam", desc:"Retake as many times as needed — no charge, no limit — until you pass." },
    { title:"Results transmitted automatically", desc:"A passing result is sent straight to the Platform. No manual submission." },
    { title:"Verified PPCA Badge issued", desc:"Attached to your profile with a unique QR code employers can scan to verify." },
    { title:"Done — nothing further required", desc:"Once issued, your PPCA badge obligations are fully satisfied. No further quiz retakes." },
  ];
  const stateSteps = [
    { title:"Take your State Exam", desc:"Retake as many times as needed until you pass." },
    { title:"Upload cert + expiration date", desc:"Required to activate your Learning Cycle after passing." },
    { title:"Learning Cycle activated", desc:"Grants continued access to Crash Course materials — no further quiz retakes during the active cycle." },
    { title:"Certification expires", desc:"Crash Course access pauses. Renew your state cert to reactivate the cycle. Content updates automatically when state regulations change." },
  ];

  return (
    <div style={{ marginTop:32 }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, fontWeight:800, color:C.blue, letterSpacing:".12em", marginBottom:8 }}>POLICY</div>
        <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:800, color:C.text }}>Certification Policy (PPCA & State Exam)</h3>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:20, marginBottom:24 }}>
        <AccessTimeline title="PPCA Certification — Access Timeline" color={C.gold} steps={ppcaSteps} />
        <AccessTimeline title="State Exam Certification — Access Timeline" color={C.blue} steps={stateSteps} />
      </div>

      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"24px 26px" }}>
        <PolicyBlock title="PPCA Certification">
          Upon passing the PPCA exam, your results are automatically transmitted to the Platform. A Verified PPCA Badge will be issued and attached to your profile, including a unique QR code for employer verification. Once issued, your PPCA badge obligations are fully satisfied and no further quiz retakes are required. Users who fail the PPCA exam may retake the exam until a passing result is achieved. Course access may be reset at no additional charge for users who have completed 100% of required course modules.
        </PolicyBlock>
        <PolicyBlock title="State Exam Certification">
          Users may retake their State Exam until a passing result is achieved. After passing, users must upload their certification documentation and expiration date to activate their Learning Cycle. The Learning Cycle grants continued access to Crash Course materials until the certification expires. Because the user has already passed the exam, no further quiz retakes are required during the active cycle. State regulations may change periodically, and Crash Course content will be updated to reflect current requirements.
        </PolicyBlock>
        <PolicyBlock title="Pass Guarantee">
          The Pass Guarantee applies only to enrolled users who have completed 100% of course content prior to taking their PPCA or State Exam. The Platform does not provide monetary refunds under any circumstances.
        </PolicyBlock>
        <div>
          <div style={{ fontSize:13.5, fontWeight:800, color:C.red, marginBottom:6 }}>Fraud & Misrepresentation</div>
          <div style={{ fontSize:12.5, color:C.textMid, lineHeight:1.75 }}>
            Users are responsible for ensuring the accuracy of all uploaded documentation. Fraudulent, falsified, or misleading certification submissions may result in suspension or termination of Platform access and revocation of badges or learning cycles.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HOW TO PASS ─────────────────────────────────────────────────────────────
function HowToPassSection({ certType }) {
  const steps = {
    reciprocity:[
      { n:1, title:"Enroll in the 13-State Network Course", desc:"Choose the 13-State Reciprocity or PPCA Master course. Both prepare you for the network's certification exam format.", icon:"📚" },
      { n:2, title:"Complete All Modules", desc:"Work through all video + text modules at your own pace. Average completion time: 4–8 hours.", icon:"▶️" },
      { n:3, title:"Pass Practice Exams", desc:"Score 70%+ on practice exams before booking your real exam. Most students pass in 1–2 attempts.", icon:"📝" },
      { n:4, title:"Take the Official Exam", desc:"Book your exam through the official certifying authority. Bring valid ID + proof of course completion.", icon:"🏛️" },
      { n:5, title:"Receive Certificate + Work!", desc:"Your certificate is recognized across all 13 network states. A few members (like Florida and Pennsylvania) require an extra local course on top — check your target state's note first.", icon:"🏆" },
    ],
    independent:[
      { n:1, title:"Enroll in the Independent States Course", desc:"New York, Oregon, and South Dakota each require their own separate certification — no other state's cert substitutes here.", icon:"⭐" },
      { n:2, title:"Study Each State's Specific Rules", desc:"New York in particular has stricter metro rules, a $1M CSL insurance requirement, and specific police-escort thresholds.", icon:"📋" },
      { n:3, title:"Pass Each State's Practice Exam", desc:"Each state's exam format differs — our practice questions are built to mirror each one specifically.", icon:"📝" },
      { n:4, title:"Schedule Each State's Official Exam", desc:"Apply directly through that state's certifying authority. New York in particular can fine operators for using an out-of-state credential.", icon:"🏛️" },
      { n:5, title:"Get Certified + Work Legally", desc:"Carry the specific state's credential at all times — none of these three are transferable to any other state.", icon:"✅" },
    ],
    ppca:[
      { n:1, title:"Enroll in PPCA Master Course", desc:"The complete 50-state + DC prep course. Covers all 3 real categories and every state's specific rules.", icon:"📚" },
      { n:2, title:"Learn the 3-Category Framework", desc:"13-State Reciprocity Network, 3 Independent Certification States, and 35 No-Cert-Required states. Master this and much of the exam becomes straightforward.", icon:"🗺️" },
      { n:3, title:"Memorize the Trip-Up States", desc:"NY, OR, SD (independent certs), CA (no cert, but a dense permit system), HI (unusually low width threshold), and DC (federal-land overlay) — these are the states that catch even experienced operators. Learn each one cold.", icon:"🚨" },
      { n:4, title:"Pass 3 Practice Exams", desc:"Score 70%+ consistently before booking your real exam. Most students are ready after 6–8 hours of study.", icon:"📝" },
      { n:5, title:"Receive PPCA Cert + Get Hired!", desc:"PPCA certification is recognized by carriers and companies across the country.", icon:"🏆" },
    ],
  };
  const list = steps[certType] || steps.ppca;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
      {list.map((step, i) => (
        <div key={step.n} style={{ display:"flex", gap:16, alignItems:"flex-start", padding:"16px 0", borderBottom: i < list.length-1 ? `1px dashed ${C.border}` : "none" }}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0 }}>
            <div style={{ width:44, height:44, borderRadius:12, background: i === 0 ? "#f59e0b" : `${C.blue}18`, border: i === 0 ? "none" : `1.5px solid ${C.blue}30`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:18, color: i === 0 ? "#0d1f3c" : C.blue }}>
              {step.icon}
            </div>
            {i < list.length-1 && <div style={{ width:2, flex:1, minHeight:20, background:C.border, margin:"4px 0" }} />}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <span style={{ fontSize:11, fontWeight:800, color:C.textSub }}>STEP {step.n}</span>
            </div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:4 }}>{step.title}</div>
            <div style={{ fontSize:13, color:C.textSub, lineHeight:1.7 }}>{step.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── ENROLL MODAL ─────────────────────────────────────────────────────────────
function EnrollModal({ course, close, onEnroll }) {
  if (!course) return null;
  return (
    <div style={{ position:"fixed", inset:0, zIndex:999, background:"rgba(13,31,60,0.60)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={close}>
      <div onClick={e=>e.stopPropagation()} style={{ background:C.surface, borderRadius:24, padding:32, maxWidth:460, width:"100%", boxShadow:"0 24px 60px rgba(13,31,60,0.30)" }}>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:course.color, letterSpacing:".1em", marginBottom:6 }}>{course.tag}</div>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:800, color:C.text, marginBottom:6 }}>{course.title}</h2>
          <StarRow rating={course.rating} count={course.students} />
        </div>

        <div style={{ background:`${course.color}10`, border:`1px solid ${course.color}25`, borderRadius:12, padding:"16px 18px", marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontSize:14, color:C.textMid, fontWeight:600 }}>Course Price</span>
            <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:28, fontWeight:900, color:C.text }}>${course.price}</span>
              <span style={{ fontSize:13, color:C.textLight, textDecoration:"line-through" }}>${course.originalPrice}</span>
            </div>
          </div>
          <div style={{ fontSize:12, color:C.green, fontWeight:700 }}>✓ One-time payment · Pass guarantee</div>
        </div>

        <div style={{ marginBottom:20 }}>
          {course.includes.slice(0,5).map((item, i) => (
            <div key={i} style={{ display:"flex", gap:8, fontSize:13, color:C.textMid, padding:"4px 0" }}>
              <span style={{ color:course.color, fontWeight:800 }}>✓</span><span>{item}</span>
            </div>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <button onClick={close} style={{ padding:"12px", borderRadius:10, border:`1px solid ${C.border}`, background:C.surfaceAlt, color:C.text, fontWeight:600, fontSize:14, cursor:"pointer" }}>Cancel</button>
          <button style={{ padding:"12px", borderRadius:10, border:"none", background:course.color, color: course.color === C.gold ? C.navy : "#fff", fontWeight:800, fontSize:14, cursor:"pointer", boxShadow:`0 4px 14px ${course.color}30` }}
          onClick={() => { onEnroll(course); close(); }}>
            Enroll Now →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── STATE MAP (from CertPage) ────────────────────────────────────────────────
const STATE_PATHS = {
  WA:"M 72 32 L 180 38 L 185 68 L 165 78 L 72 72 Z",
  OR:"M 68 72 L 182 72 L 182 130 L 68 130 Z",
  CA:"M 62 130 L 155 130 L 162 220 L 118 268 L 72 240 Z",
  NV:"M 155 98 L 210 95 L 215 220 L 162 220 L 155 130 Z",
  AZ:"M 162 220 L 258 220 L 258 292 L 162 292 Z",
  NM:"M 258 222 L 330 222 L 330 295 L 258 295 Z",
  CO:"M 262 165 L 390 165 L 390 220 L 262 220 Z",
  UT:"M 210 120 L 265 120 L 265 225 L 210 225 Z",
  TX:"M 295 235 L 490 235 L 500 340 L 400 365 L 310 305 Z",
  OK:"M 330 210 L 505 210 L 505 248 L 330 248 Z",
  KS:"M 330 175 L 500 175 L 500 215 L 330 215 Z",
  MN:"M 430 60 L 530 60 L 530 130 L 430 130 Z",
  WI:"M 510 82 L 575 82 L 575 128 L 510 128 Z",
  IL:"M 530 128 L 585 128 L 585 202 L 530 202 Z",
  IN:"M 585 118 L 628 118 L 628 192 L 585 192 Z",
  OH:"M 628 98 L 675 98 L 675 180 L 628 180 Z",
  PA:"M 658 112 L 738 112 L 738 158 L 658 158 Z",
  NY:"M 680 72 L 775 72 L 775 120 L 680 120 Z",
  VA:"M 652 178 L 730 178 L 730 215 L 652 215 Z",
  NC:"M 640 215 L 745 215 L 745 248 L 640 248 Z",
  GA:"M 610 255 L 678 255 L 678 328 L 610 328 Z",
  FL:"M 575 318 L 665 318 L 685 398 L 608 405 L 578 370 Z",
  TN:"M 572 228 L 672 228 L 672 262 L 572 262 Z",
  KY:"M 572 195 L 672 195 L 672 230 L 572 230 Z",
  MO:"M 460 175 L 555 175 L 555 240 L 460 240 Z",
  IA:"M 430 130 L 530 130 L 530 172 L 430 172 Z",
  NE:"M 330 135 L 465 135 L 465 178 L 330 178 Z",
  SD:"M 330 88 L 465 88 L 465 138 L 330 138 Z",
  ND:"M 330 50 L 465 50 L 465 90 L 330 90 Z",
  MT:"M 188 28 L 388 28 L 388 88 L 188 88 Z",
  WY:"M 240 90 L 390 90 L 390 160 L 240 160 Z",
  ID:"M 182 38 L 255 38 L 255 145 L 182 145 Z",
  LA:"M 452 318 L 545 318 L 545 380 L 452 370 Z",
  MD:"M 698 158 L 738 158 L 738 178 L 698 178 Z",
  MA:"M 750 88 L 808 88 L 808 110 L 750 110 Z",
  ME:"M 755 38 L 808 38 L 808 78 L 755 78 Z",
  WV:"M 638 162 L 675 162 L 675 200 L 638 200 Z",
};

const STATE_LABELS = [
  ["WA",118,56],["OR",120,100],["CA",108,196],["NV",183,158],["AZ",210,256],
  ["NM",294,258],["CO",326,190],["UT",237,172],["TX",397,300],["OK",417,230],
  ["KS",415,195],["MN",480,95],["IL",557,165],["OH",651,138],["PA",698,135],
  ["NY",727,96],["VA",691,195],["NC",692,231],["GA",644,292],["FL",630,358],
  ["TN",622,244],["KY",622,212],["MO",507,207],["IA",480,151],["NE",397,156],
  ["SD",397,113],["ND",397,70],["MT",288,58],["WY",315,125],["ID",218,90],
  ["LA",498,348],["WV",656,180],["MA",779,99],["ME",781,58],
];

function StateMap({ isDarkMode, onSelectState }) {
  const [hovered, setHovered] = React.useState(null);
  const D = isDarkMode;
  const oceanBg = D ? "#0a1628" : "#dde8f0";
  const gridLine = D ? "rgba(255,255,255,0.06)" : "#c8d8e4";
  const tooltipBg = D ? "#142a4d" : "#ffffff";
  const tooltipBorder = D ? "rgba(255,255,255,0.12)" : "#e2e8f0";
  const tooltipText = D ? "#f0f4f8" : "#0d1f3c";
  const tooltipSub = D ? "rgba(240,244,248,0.65)" : "#475569";
  const noStateFill = D ? "#1e2d40" : "#dde8f0";
  const noStateStroke = D ? "#2a3d54" : "#b8cdd9";
  const legendText = D ? "rgba(240,244,248,0.65)" : "#475569";

  const getColor = (code) => {
    const s = STATES_DATA[code];
    if (!s) return { fill: noStateFill, stroke: noStateStroke };
    if (s.inNetwork)   return { fill: "#0891b2aa", stroke: "#0891b2" };
    if (s.independent) return { fill: "#d97706cc", stroke: "#d97706" };
    return { fill: "#64748b88", stroke: "#64748b" }; // no certification required
  };

  const h = hovered && STATES_DATA[hovered];

  return (
    <div style={{ position:"relative" }}>
      <span style={{ position:"absolute", width:1, height:1, padding:0, margin:-1, overflow:"hidden", clip:"rect(0,0,0,0)", whiteSpace:"nowrap", border:0 }}>
        Certification map. {Object.entries(STATES_DATA).map(([code, s]) => `${s.name}: ${s.inNetwork ? "13-state reciprocity network" : s.independent ? "independent certification required" : "no certification required"}.`).join(" ")}
      </span>
      <svg viewBox="0 0 820 420" role="img" aria-label="US map color-coded by pilot car certification requirement per state" style={{ width:"100%", display:"block", borderRadius:14, background:oceanBg, border:`1px solid ${tooltipBorder}`, transition:"background .3s" }}>
        {[1,2,3,4,5,6].map(i => <line key={`h${i}`} x1={0} y1={i*70} x2={820} y2={i*70} stroke={gridLine} strokeWidth={0.5}/>)}
        {[1,2,3,4,5,6,7,8,9].map(i => <line key={`v${i}`} x1={i*91} y1={0} x2={i*91} y2={420} stroke={gridLine} strokeWidth={0.5}/>)}
        {Object.entries(STATE_PATHS).map(([code, path]) => {
          const { fill, stroke } = getColor(code);
          const isHov = hovered === code;
          const clickable = !!STATES_DATA[code];
          return (
            <path key={code} d={path} fill={fill} stroke={stroke} strokeWidth={isHov ? 2 : 0.8}
              style={{ cursor: clickable ? "pointer" : "default", transition:"all .12s", filter:isHov?"brightness(0.80)":"none" }}
              onMouseEnter={() => setHovered(code)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => clickable && onSelectState && onSelectState(code)}/>
          );
        })}
        {STATE_LABELS.map(([code, x, y]) => {
          const s = STATES_DATA[code];
          if (!s) return null;
          return (
            <text key={code} x={x} y={y} textAnchor="middle" fontSize={9} fontWeight="700"
              fontFamily="Inter,sans-serif" fill="#fff"
              onClick={() => onSelectState && onSelectState(code)}
              onMouseEnter={() => setHovered(code)}
              onMouseLeave={() => setHovered(null)}
              style={{cursor:"pointer",userSelect:"none",opacity:0.9}}>
              {code}
            </text>
          );
        })}
      </svg>

      {h && (
        <div style={{ position:"absolute", top:10, right:10, background:tooltipBg, border:`1px solid ${tooltipBorder}`, borderRadius:12, padding:"12px 16px", maxWidth:240, boxShadow:"0 4px 20px rgba(0,0,0,0.18)", pointerEvents:"none", transition:"background .3s" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
            <span style={{ fontSize:18 }}>{h.flag}</span>
            <div style={{ fontWeight:800, fontSize:14, color:tooltipText }}>{h.name}</div>
          </div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:7 }}>
            {h.inNetwork && <span style={{ fontSize:10, fontWeight:700, background:"#0891b218", color:"#0891b2", padding:"2px 8px", borderRadius:99, border:"1px solid #0891b228" }}>🔵 13-State Network</span>}
            {h.independent && <span style={{ fontSize:10, fontWeight:700, background:"#d9770618", color:"#d97706", padding:"2px 8px", borderRadius:99, border:"1px solid #d9770628" }}>⭐ Independent Cert</span>}
            {h.noCert && <span style={{ fontSize:10, fontWeight:700, background:"#64748b18", color:"#64748b", padding:"2px 8px", borderRadius:99, border:"1px solid #64748b28" }}>🗺️ No Cert Required</span>}
          </div>
          <div style={{ fontSize:12, color:tooltipSub, lineHeight:1.5 }}>{h.note}</div>
        </div>
      )}

      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:12 }}>
        {[
          { color:"#0891b2aa", label:"13-State Reciprocity Network" },
          { color:"#d97706cc", label:"Independent certification required" },
          { color:"#64748b88", label:"No certification required" },
        ].map(l => (
          <div key={l.label} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:legendText }}>
            <div style={{ width:13, height:13, borderRadius:3, background:l.color, border:l.border?`1px solid ${noStateStroke}`:"none", flexShrink:0 }}/>
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN TRAINING PAGE ───────────────────────────────────────────────────────
export default function TrainingHub({ isDarkMode, toggleTheme }) {
  C = isDarkMode ? DARK_C : LIGHT_C;
  const COURSES = buildCourses();
  const MEMBERSHIPS = buildMemberships();
  const [tab, setTab] = useState("courses");
  const [certType, setCertType] = useState("reciprocity");
  const [enrollCourse, setEnrollCourse] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0,0); }, []);

  // Routes into signup with the chosen course pre-selected, instead of the
  // old dead-end alert() — matches the enrollment flow used on /pricing.
  const handleEnroll = (course) => {
    navigate(`/signup?course=${encodeURIComponent(course.title)}`);
  };

  // Clicking a state on the map jumps to "All State Certs" with that
  // state's requirement pre-selected and its detail card shown.
  const handleStateSelect = (code) => {
    const s = STATES_DATA[code];
    if (!s) return;
    setSelectedState(code);
    setCertType(s.inNetwork ? "reciprocity" : s.independent ? "independent" : "noCertRequired");
    setTab("states");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@700;800;900&display=swap');
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; overflow-x: hidden; }
        .th-tabnav::-webkit-scrollbar { display: none; }
        button, a { -webkit-tap-highlight-color: transparent; }
        button:focus-visible, a:focus-visible {
          outline: 2px solid #c9971c;
          outline-offset: 2px;
          border-radius: 4px;
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { transition: none !important; animation: none !important; }
        }
        @media (max-width: 640px) {
          .th-hero-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .th-feature-grid { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 641px) and (max-width: 980px) {
          .th-feature-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

    {/* <CheatSheets/> */}
      {/* HERO */}
      <div style={{ background:"linear-gradient(135deg, #0d1f3c 0%, #1a3358 60%, #0f2d56 100%)", padding:"52px 24px 0", borderBottom:"3px solid #c9971c" }}>
        <div style={{ maxWidth:1160, margin:"0 auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", flexWrap:"wrap", gap:24, paddingBottom:32 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:800, color:"#c9971c", letterSpacing:".14em", marginBottom:12 }}>PILOTLINK USA</div>
              <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(28px,5vw,48px)", fontWeight:900, color:"#fff", margin:"0 0 12px", letterSpacing:"-.025em", lineHeight:1.1 }}>
                State Certifications &<br /><span style={{ background:"linear-gradient(90deg,#c9971c,#fde68a)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>Platform Features</span>
              </h1>
              <p style={{ fontSize:15, color:"rgba(255,255,255,0.75)", maxWidth:520, lineHeight:1.75, margin:0 }}>
                13-state reciprocity network, 3 independent-certification states, and every other state's rules. Everything you need to get certified, stay compliant, and get hired.
              </p>
            </div>
            <div className="th-hero-stats" style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, flexShrink:0 }}>
              {[["50","States Covered","overview"],["4","Compliance Groups","overview"],["6","Courses Available","courses"],["70%","Passing Score","how"]].map(([n,l,goTab]) => (
                <button key={l} onClick={() => setTab(goTab)} style={{
                  padding:"14px 18px", borderRadius:12, background:"rgba(255,255,255,0.07)",
                  border:"1px solid rgba(255,255,255,0.12)", textAlign:"center", minWidth:90,
                  cursor:"pointer", fontFamily:"inherit", transition:"all .15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.14)"; e.currentTarget.style.transform="translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.07)"; e.currentTarget.style.transform="none"; }}>
                  <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:24, fontWeight:900, color:"#c9971c", lineHeight:1 }}>{n}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.75)", marginTop:4, fontWeight:500 }}>{l}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab nav */}
          <div role="tablist" aria-label="Training Tab sections" className="th-tabnav" style={{ display:"flex", gap:0, overflowX:"auto", scrollbarWidth:"none" }}>
            {TABS.map(t => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} aria-label={t.label} onClick={() => setTab(t.id)} style={{
                padding:"12px 16px", border:"none", background:"transparent", cursor:"pointer",
                color: tab === t.id ? "#c9971c" : "rgba(255,255,255,0.55)",
                fontWeight: tab === t.id ? 700 : 400, fontSize:13,
                borderBottom:`2.5px solid ${tab === t.id ? "#c9971c" : "transparent"}`,
                display:"flex", alignItems:"center", gap:6, transition:"all .15s", whiteSpace:"nowrap",
                fontFamily:"'Inter',sans-serif",
              }}>
                <span aria-hidden="true">{t.icon}</span>{t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div style={{ background:C.page, minHeight:"60vh", transition:"background .3s" }}>
        <div style={{ maxWidth:1160, margin:"0 auto", padding:"36px 24px 72px" }}>

          {/* ── COURSES TAB ── */}
          {tab === "courses" && (
            <div>
              {/* Banner */}
              <div style={{ background:"linear-gradient(135deg, #0d1f3c, #1a3358)", borderRadius:20, padding:"28px 32px", marginBottom:32, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:20 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#c9971c", letterSpacing:".12em", marginBottom:8 }}>🎓 ONLINE COURSES — INSTANT ACCESS</div>
                  <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:24, fontWeight:800, color:"#ffffff", margin:"0 0 8px" }}>Get Certified. Get Hired. Earn More.</h2>
                  <p style={{ fontSize:14, color:"rgba(255,255,255,0.75)", margin:0, lineHeight:1.7 }}>Professional pilot car certification prep. Every course includes a pass guarantee. Most students pass on their first attempt.</p>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <div style={{ textAlign:"center", padding:"12px 20px", background:"rgba(255,255,255,0.08)", borderRadius:12, border:"1px solid rgba(255,255,255,0.12)" }}>
                    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:900, color:"#c9971c" }}>9,000+</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.75)" }}>Students enrolled</div>
                  </div>
                  <div style={{ textAlign:"center", padding:"12px 20px", background:"rgba(255,255,255,0.08)", borderRadius:12, border:"1px solid rgba(255,255,255,0.12)" }}>
                    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:22, fontWeight:900, color:"#c9971c" }}>4.9★</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.75)" }}>Average rating</div>
                  </div>
                </div>
              </div>

              {/* Featured courses (highlight ones first) */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap:20 }}>
                {[...COURSES.filter(c=>c.highlight), ...COURSES.filter(c=>!c.highlight)].map(course => (
                  <CourseCard key={course.id} course={course} onBuy={setEnrollCourse} />
                ))}
              </div>

              {/* Trust strip */}
              <div style={{ marginTop:32, background:C.surface, borderRadius:16, padding:"20px 24px", border:`1px solid ${C.border}`, display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center" }}>
                {[["🛡️","Pass Guarantee","Fail? Retake free, no questions asked"],["💳","One-Time Payment","No subscriptions, ever"],["⚡","Instant Access","Start learning within 60 seconds"],["📱","Learn Anywhere","Mobile, tablet, or desktop"]].map(([icon, title, sub]) => (
                  <div key={title} style={{ display:"flex", alignItems:"center", gap:10, minWidth:160, flex:"1 1 140px" }}>
                    <span style={{ fontSize:22 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{title}</div>
                      <div style={{ fontSize:12, color:C.textMid }}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── HOW TO PASS TAB ── */}
          {tab === "how" && (
            <div>
              <div style={{ marginBottom:32 }}>
                <div style={{ fontSize:11, fontWeight:800, color:C.blue, letterSpacing:".12em", marginBottom:8 }}>PPCA CERTIFICATION</div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:32, fontWeight:800, color:C.text, marginBottom:10 }}>How to Get PPCA Certified & Verified</h2>
                <p style={{ fontSize:15, color:C.textSub, lineHeight:1.75 }}>PPCA is what unlocks your PilotLink <strong style={{ color:C.text }}>Verified Badge</strong> — the credential companies search for. Here's exactly how to earn it, step by step.</p>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))", gap:24, alignItems:"start" }}>
                <div style={{ background:C.surface, borderRadius:16, border:`1px solid ${C.border}`, padding:"24px 28px", boxShadow:"0 2px 8px rgba(13,31,60,0.06)" }}>
                  <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18, fontWeight:800, color:C.text, marginBottom:20 }}>
                    PPCA Certification — Step by Step
                  </h3>
                  <HowToPassSection certType="ppca" />
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  <div style={{ background:C.greenLight, border:`1px solid ${C.green}25`, borderRadius:16, padding:"20px 22px" }}>
                    <div style={{ fontSize:22, marginBottom:8 }}>🏆</div>
                    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:16, color:C.text, marginBottom:6 }}>Pass Guarantee</div>
                    <div style={{ fontSize:13, color:C.textMid, lineHeight:1.7 }}>Complete all modules and fail your first exam? Full course access reset — free. Study again and pass.</div>
                  </div>
                  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 22px" }}>
                    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:15, color:C.text, marginBottom:14 }}>After Passing You Get</div>
                    {["Verified badge on PilotLink profile","State compliance PDF checklist","Direct permit office links","How-to-set-up-your-company guide","State cheat sheets (login-only)","Discount on additional certs"].map(f => (
                      <div key={f} style={{ display:"flex", gap:8, padding:"6px 0", borderBottom:`1px solid ${C.border}`, fontSize:13, color:C.textMid }}>
                        <span style={{ color:C.green, fontWeight:700, flexShrink:0 }}>✓</span>{f}
                      </div>
                    ))}
                  </div>
                  <div style={{ background:C.amberLight, border:`1px solid ${C.amber}30`, borderRadius:16, padding:"18px 20px" }}>
                    <div style={{ fontSize:11, fontWeight:800, color:C.amber, letterSpacing:".08em", marginBottom:8 }}>WHAT THIS COURSE IS — AND ISN'T</div>
                    <div style={{ fontSize:12.5, color:C.textMid, lineHeight:1.75 }}>
                      Passing a PilotLink course gets you a <strong style={{ color:C.text }}>certificate of completion</strong> and,
                      if you're a Pro Driver member, a <strong style={{ color:C.text }}>Verified Badge</strong> — proof to companies
                      that you've studied and tested on the current requirements. It is <strong style={{ color:C.text }}>not</strong> itself
                      the official state or network credential. For 13-state network and independent-certification states, you still
                      complete the official certifying body's exam separately — this course is built to prepare you for it, not replace it.
                    </div>
                  </div>
                  <div style={{ background:C.navyMid, borderRadius:16, padding:"20px 22px" }}>
                    <div style={{ fontSize:11, fontWeight:800, color:"#c9971c", letterSpacing:".1em", marginBottom:8 }}>ALREADY CERTIFIED?</div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,0.70)", lineHeight:1.7, marginBottom:14 }}>Already hold a network or state cert? Upload it, then pass a quick rules quiz so we can confirm you're current on today's requirements. Pass it and your Verified Badge unlocks — no full course required.</div>
                    <button onClick={() => { setTab("verify"); window.scrollTo({ top: 0, behavior: "smooth" }); }} style={{ display:"block", textAlign:"center", width:"100%", padding:"11px", borderRadius:10, border:"none", background:"#c9971c", color:C.navy, fontWeight:800, fontSize:14, cursor:"pointer", boxSizing:"border-box", fontFamily:"inherit" }}>Start Verification →</button>
                  </div>
                </div>
              </div>
              <CertificationPolicySection />
              <Disclaimer />
            </div>
          )}

          {/* ── MEMBERSHIP TAB ── */}
          {tab === "membership" && (
            <div>
              <div style={{ textAlign:"center", marginBottom:40 }}>
                <div style={{ fontSize:11, fontWeight:800, color:C.blue, letterSpacing:".12em", marginBottom:8 }}>JOIN PILOTLINK</div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:32, fontWeight:800, color:C.text, marginBottom:10 }}>Membership & Pricing</h2>
                <p style={{ fontSize:15, color:C.textSub, lineHeight:1.75, maxWidth:540, margin:"0 auto" }}>No PPCA required to become a member. Already certified? Upload your cert and get verified instantly.</p>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:20 }}>
                {MEMBERSHIPS.map(m => <MembershipCard key={m.id} m={m} />)}
              </div>
              <Disclaimer />
            </div>
          )}

          {/* ── CHEAT SHEETS TAB ── */}
          {tab === "cheatsheet" && (
            <div>
              <div style={{ background:"linear-gradient(135deg, #0d1f3c, #1a3358)", borderRadius:20, padding:"60px 40px", textAlign:"center" }}>
                <div style={{ fontSize:48, marginBottom:16 }}>🔒</div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:28, fontWeight:800, color:"#fff", marginBottom:10 }}>Cheat Sheets — Members Only</h2>
                <p style={{ fontSize:15, color:"rgba(255,255,255,0.70)", maxWidth:500, margin:"0 auto 28px", lineHeight:1.75 }}>State compliance guides, equipment requirements, curfew rules, and permit links. Login-protected. Cannot be downloaded or copied.</p>
                <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
                  <Link to="/signup" style={{ padding:"13px 28px", borderRadius:99, background:"#c9971c", color:"#0d1f3c", fontWeight:800, fontSize:15, textDecoration:"none", boxShadow:"0 4px 14px rgba(201,151,28,0.35)" }}>Join as Member</Link>
                  <Link to="/login" style={{ padding:"13px 28px", borderRadius:99, border:"1.5px solid rgba(255,255,255,0.25)", color:"#fff", fontWeight:700, fontSize:15, textDecoration:"none" }}>Sign In</Link>
                </div>
              </div>
              <Disclaimer />
            </div>
          )}

          {/* ── OVERVIEW TAB ── */}
          {tab === "overview" && (
            <div>
              <div style={{ marginBottom:28 }}>
                <div style={{ fontSize:11, fontWeight:800, color:C.blue, letterSpacing:".12em", marginBottom:8 }}>CERTIFICATION MAP</div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:32, fontWeight:800, color:C.text, marginBottom:10 }}>Which States Require What?</h2>
                <p style={{ fontSize:15, color:C.textSub, lineHeight:1.7 }}>Hover over any state for a quick preview, or click it to jump straight to its certification details below. Color-coded: PEVO (teal), Standalone (amber), PPCA/Compliance (purple).</p>
              </div>

              {/* Legend cards */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14, marginBottom:24 }}>
                {[
                  { color:"#0891b2", title:"🔵 13-State Reciprocity Network", sub:"AZ, CO, FL, GA, KS, MN, NC, OK, PA, TX, UT, VA, WA", note:"One certificate is recognized across all 13 states. A few members add a small local requirement on top." },
                  { color:"#d97706", title:"⭐ Independent Certification States", sub:"New York, Oregon, South Dakota", note:"Each requires its own separate certification — no reciprocity into the network or with each other." },
                  { color:"#64748b", title:"🗺️ No Certification Required", sub:"35 states + DC", note:"No state exam required by law — but escort triggers, insurance minimums, and equipment rules still apply." },
                ].map(x => (
                  <div key={x.title} style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:`3px solid ${x.color}`, borderRadius:12, padding:"14px 16px" }}>
                    <div style={{ fontWeight:800, fontSize:14, color:C.text, marginBottom:4 }}>{x.title}</div>
                    <div style={{ fontSize:11, fontWeight:700, color:x.color, marginBottom:6 }}>{x.sub}</div>
                    <div style={{ fontSize:12, color:C.textSub, lineHeight:1.5 }}>{x.note}</div>
                  </div>
                ))}
              </div>

              {/* Interactive SVG Map */}
              <StateMap isDarkMode={isDarkMode} onSelectState={handleStateSelect} />

              {/* Quick-enroll summary cards */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:16, marginTop:28 }}>
                {[
                  { icon:"🔵", title:"13-State Network", color:"#0891b2", colorL:C.tealLight, desc:"One certificate accepted across all 13 reciprocity-network states.", states:"AZ · CO · FL · GA · KS · MN · NC + 6 more", price:"From $149" },
                  { icon:"⭐", title:"Independent States", color:"#d97706", colorL:C.amberLight, desc:"New York, Oregon, and South Dakota each require their own separate certification.", states:"NY · OR · SD", price:"From $179" },
                  { icon:"🏆", title:"PPCA Master — All 50 + DC", color:"#7c3aed", colorL:C.purpleLight, desc:"Covers the network, the independent states, and every no-cert-required state's rules — all in one course.", states:"All 50 states + DC", price:"From $199" },
                ].map(card => (
                  <div key={card.title} style={{ background:C.surface, border:`1px solid ${C.border}`, borderTop:`4px solid ${card.color}`, borderRadius:16, padding:"22px 24px" }}>
                    <div style={{ fontSize:28, marginBottom:10 }}>{card.icon}</div>
                    <h3 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:17, color:C.text, marginBottom:8 }}>{card.title}</h3>
                    <p style={{ fontSize:13, color:C.textMid, lineHeight:1.7, marginBottom:12 }}>{card.desc}</p>
                    <div style={{ fontSize:11, fontWeight:700, color:card.color, marginBottom:14 }}>{card.states}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:16, fontWeight:800, color:C.text }}>{card.price}</span>
                      <button onClick={() => setTab("courses")} style={{ padding:"8px 18px", borderRadius:99, border:"none", background:card.color, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>Enroll →</button>
                    </div>
                  </div>
                ))}
              </div>
              <Disclaimer />
            </div>
          )}

          {/* ── FEATURES TAB ── */}
          {tab === "features" && (
            <div>
              <div style={{ marginBottom:32 }}>
                <div style={{ fontSize:11, fontWeight:800, color:C.blue, letterSpacing:".12em", marginBottom:8 }}>PLATFORM</div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:32, fontWeight:800, color:C.text, marginBottom:10 }}>Everything Pilot Car Pros Need</h2>
                <p style={{ fontSize:15, color:C.textSub }}>Every tool built specifically for oversize load escort professionals.</p>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:16 }} className="th-feature-grid">
                {[
                  { icon:"🎓", title:"Certification Training", color:C.teal, desc:"PPCA, PEVO, NY standalone. Pre-exam practice tests. Pass guarantee included.", items:["8–12 modules per course","Unlimited practice tests","State law deep-dives","Pass guarantee"], tabTarget:"courses" },
                  { icon:"✅", title:"Verified Driver Profile", color:C.green, desc:"Upload certs, insurance, vehicle, and equipment. Companies see everything to hire you.", items:["Upload certs per state","Vehicle + equipment photos","Availability toggle","Instant company visibility"], to:"/get-verified" },
                  { icon:"💼", title:"Live Load Board", color:C.blue, desc:"Real-time oversize load listings. Filter by state, cert type, and pay. Apply in one click.", items:["Real-time notifications","Filter by cert + state","One-click apply","Direct company contact"], to:"/load-board" },
                  { icon:"🛒", title:"Equipment Marketplace", color:C.amber, desc:"Buy and sell oversize signs, strobes, height poles — from verified sellers.", items:["Verified seller badges","Category search","In-app messaging","20 free listings/month"], to:"/marketplace" },
                  { icon:"📄", title:"Invoice Builder", color:C.purple, desc:"Generate professional invoices in seconds. Send directly to the company.", items:["Auto-calculate mileage","1-click send to company","Invoice history","Payment tracking"], to:"/signup" },
                ].map(f => {
                  const cardStyle = {
                    background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"22px 24px",
                    borderTop:`3px solid ${f.color}`, boxShadow:"0 2px 6px rgba(13,31,60,0.05)",
                    display:"flex", flexDirection:"column", gap:12, cursor:"pointer", textDecoration:"none",
                    transition:"all .15s",
                  };
                  const hoverIn = (e) => { e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow=`0 10px 26px ${f.color}25`; e.currentTarget.style.borderColor=f.color; };
                  const hoverOut = (e) => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="0 2px 6px rgba(13,31,60,0.05)"; e.currentTarget.style.borderColor=C.border; };
                  const inner = (
                    <>
                      <div style={{ fontSize:28 }} aria-hidden="true">{f.icon}</div>
                      <div>
                        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:17, color:C.text, marginBottom:6 }}>{f.title}</div>
                        <div style={{ fontSize:13, color:C.textSub, lineHeight:1.7 }}>{f.desc}</div>
                      </div>
                      <div>
                        {f.items.map(item => (
                          <div key={item} style={{ display:"flex", gap:7, fontSize:12, color:C.textMid, marginBottom:3 }}>
                            <span style={{ color:f.color, fontWeight:800 }} aria-hidden="true">→</span>{item}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                  return f.tabTarget ? (
                    <button key={f.title} onClick={() => { setTab(f.tabTarget); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      aria-label={`Open ${f.title}`} style={{ ...cardStyle, textAlign:"left", fontFamily:"inherit" }}
                      onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                      {inner}
                    </button>
                  ) : (
                    <Link key={f.title} to={f.to} aria-label={`Open ${f.title}`} style={cardStyle}
                      onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                      {inner}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STATES TAB (simplified) ── */}
          {tab === "states" && (
            <div>
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:11, fontWeight:800, color:C.blue, letterSpacing:".12em", marginBottom:8 }}>ALL STATE CERTIFICATIONS</div>
                <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:32, fontWeight:800, color:C.text, marginBottom:10 }}>Choose Your State</h2>
                <p style={{ fontSize:15, color:C.textSub }}>Select a cert type below, or click any state on the <button onClick={() => setTab("overview")} style={{ background:"none", border:"none", padding:0, color:C.blue, fontWeight:700, cursor:"pointer", fontSize:15, textDecoration:"underline", fontFamily:"inherit" }}>certification map</button> to jump straight here.</p>
              </div>

              {selectedState && STATES_DATA[selectedState] && (
                <div style={{ background:C.surface, border:`1.5px solid ${C.gold}55`, borderRadius:16, padding:"18px 22px", marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <span style={{ fontSize:30 }}>{STATES_DATA[selectedState].flag}</span>
                    <div>
                      <div style={{ fontSize:16, fontWeight:800, color:C.text }}>{STATES_DATA[selectedState].name}</div>
                      <div style={{ fontSize:12.5, color:C.textSub, marginTop:2, lineHeight:1.5 }}>{STATES_DATA[selectedState].note}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <button onClick={() => setTab("courses")} style={{ padding:"9px 18px", borderRadius:99, border:"none", background:C.gold, color:C.navy, fontWeight:800, fontSize:13, cursor:"pointer", whiteSpace:"nowrap" }}>Enroll for {STATES_DATA[selectedState].name} →</button>
                    <button onClick={() => setSelectedState(null)} style={{ padding:"9px 14px", borderRadius:99, border:`1px solid ${C.border}`, background:"transparent", color:C.textSub, fontWeight:700, fontSize:13, cursor:"pointer" }}>Clear</button>
                  </div>
                </div>
              )}

              <div style={{ display:"flex", gap:10, marginBottom:24, flexWrap:"wrap" }}>
                {[["reciprocity","🔵 13-State Network","#0891b2"],["independent","⭐ Independent States","#d97706"],["noCertRequired","🗺️ No Cert Required","#64748b"]].map(([id,label,color]) => (
                  <button key={id} onClick={() => { setCertType(id); setSelectedState(null); }} style={{ padding:"9px 18px", borderRadius:10, border:`2px solid ${certType===id?color:C.border}`, background: certType===id?`${color}18`:C.surface, color: certType===id?color:C.textSub, fontWeight: certType===id?700:500, fontSize:13, cursor:"pointer" }}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:"16px 20px", marginBottom:16, fontSize:13, color:C.text, fontWeight:500, lineHeight:1.7 }}>
                <strong style={{ color:C.text }}>
                  {certType==="reciprocity"?"13-State Reciprocity Network — one certificate is recognized across all 13 states. Some member states add a small local requirement on top (see each state's note).":certType==="independent"?"Independent Certification States — New York, Oregon, and South Dakota each require their own separate state certification, with no reciprocity into the 13-state network or each other.":"No Certification Required — no state exam is required by law here, but escort trigger dimensions, insurance minimums, and equipment rules still apply. Always confirm current requirements with that state's DOT."}
                </strong>
              </div>

              {/* Full clickable roster of covered states */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(150px, 1fr))", gap:8, marginBottom:20 }}>
                {Object.entries(STATES_DATA).map(([code, s]) => (
                  <button key={code} onClick={() => setSelectedState(code)} style={{
                    display:"flex", alignItems:"center", gap:8, padding:"9px 12px", borderRadius:10,
                    border:`1px solid ${selectedState===code?C.gold:C.border}`,
                    background: selectedState===code ? C.goldLight : C.surface, cursor:"pointer", textAlign:"left",
                  }}>
                    <span style={{ fontSize:15 }}>{s.flag}</span>
                    <span style={{ fontSize:12.5, fontWeight:700, color:C.text }}>{s.name}</span>
                  </button>
                ))}
              </div>

              <Link to="/test-ppcastudyguide" style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"13px 24px", borderRadius:99, background:"linear-gradient(135deg,#f59e0b,#fbbf24)", color:"#0d1f3c", fontWeight:800, fontSize:14, textDecoration:"none" }}>
                Open Full Study Guide →
              </Link>
              <Disclaimer />
            </div>
          )}

          {/* ── VERIFICATION TAB (embeds the real PPCA Verification Center) ── */}
          {tab === "verify" && (
            <div>
              <button onClick={() => setTab("how")} style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:16, background:"none", border:"none", color:C.blue, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit", padding:0 }}>
                ← Back to PPCA & Verified Badge
              </button>
              <div style={{ borderRadius:20, overflow:"hidden", border:`1px solid ${C.border}`, boxShadow:"0 2px 8px rgba(13,31,60,0.06)" }}>
                <PPCAVerificationTab />
              </div>
            </div>
          )}

        </div>
      </div>

      <Footer />
      <EnrollModal course={enrollCourse} close={() => setEnrollCourse(null)} onEnroll={handleEnroll} />
    </>
  );
}
